import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { EASE_IN_OUT, EASE_OUT_EXPO } from '@/components/motion/reveal'
import { useLenis } from '@/components/motion/smooth-scroll'
import { OfficeClocks } from '@/components/generals/office-clocks'
import { companyLinks, industryLinks, siteConfig } from '@/constants/site'

const glows: Record<string, string> = {
    '/industry/crypto': 'radial-gradient(60% 70% at 70% 40%, rgba(201, 184, 255, 0.34), transparent 70%)',
    '/industry/gaming': 'radial-gradient(60% 70% at 70% 50%, rgba(53, 213, 186, 0.28), transparent 70%)',
    '/industry/prepaid-cards': 'radial-gradient(60% 70% at 70% 60%, rgba(52, 181, 255, 0.3), transparent 70%)',
    default: 'radial-gradient(55% 65% at 75% 30%, rgba(140, 95, 255, 0.32), transparent 70%)'
}

interface MenuOverlayProps {
    open: boolean
    onClose: () => void
}

export const MenuOverlay: FC<MenuOverlayProps> = ({ open, onClose }): ReactNode => {
    const lenis = useLenis()
    const panel = useRef<HTMLDivElement>(null)
    const [hovered, setHovered] = useState<string>('default')

    useEffect(() => {
        if (open) {
            lenis?.stop()
            document.documentElement.style.overflow = 'hidden'
        }
        else {
            lenis?.start()
            document.documentElement.style.overflow = ''
        }
    }, [open, lenis])

    useEffect(() => {
        return () => {
            document.documentElement.style.overflow = ''
        }
    }, [])

    useEffect(() => {
        if (!open) return

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose()
                return
            }

            if (event.key !== 'Tab' || !panel.current) return

            const focusable = Array.from(panel.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'))
            const menuButton = document.getElementById('menu-toggle')
            const items = menuButton ? [menuButton, ...focusable] : focusable

            if (items.length === 0) return

            const first = items[0]
            const last = items[items.length - 1]

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            }
            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }

        document.addEventListener('keydown', onKey)

        const timer = window.setTimeout(() => {
            panel.current?.querySelector<HTMLElement>('a[href]')?.focus({ preventScroll: true })
        }, 450)

        return () => {
            document.removeEventListener('keydown', onKey)
            window.clearTimeout(timer)
        }
    }, [open, onClose])

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    ref={panel}
                    id='site-menu'
                    role='dialog'
                    aria-modal='true'
                    aria-label='Site menu'
                    data-lenis-prevent
                    className='fixed inset-0 z-40 overflow-y-auto bg-ink text-mist'
                    initial={{ clipPath: 'circle(0% at 94% 3rem)' }}
                    animate={{ clipPath: 'circle(160% at 94% 3rem)' }}
                    exit={{ clipPath: 'circle(0% at 94% 3rem)' }}
                    transition={{ duration: 0.95, ease: EASE_IN_OUT }}
                >
                    <div
                        aria-hidden
                        className='pointer-events-none absolute inset-0 transition-[background] duration-700'
                        style={{ background: glows[hovered] ?? glows.default }}
                    />

                    <div className='relative flex min-h-full flex-col px-5 pb-8 pt-28 md:px-8 lg:px-12 lg:pt-32'>
                        <div className='grid flex-1 gap-16 lg:grid-cols-12'>
                            <nav aria-label='Industries' className='lg:col-span-8'>
                                <p className='t-label mb-6 text-mist/50'>
                                    Industries
                                </p>

                                <ul onMouseLeave={() => setHovered('default')}>
                                    {industryLinks.map((link, index) => (
                                        <li key={link.href} className='overflow-hidden border-b border-mist/10'>
                                            <motion.div
                                                initial={{ y: '105%' }}
                                                animate={{ y: '0%' }}
                                                exit={{ y: '105%' }}
                                                transition={{ duration: 0.9, ease: EASE_OUT_EXPO, delay: 0.3 + index * 0.07 }}
                                            >
                                                <Link
                                                    href={link.href}
                                                    onClick={onClose}
                                                    onMouseEnter={() => setHovered(link.href)}
                                                    onFocus={() => setHovered(link.href)}
                                                    data-cursor='Enter'
                                                    className='group flex items-baseline gap-4 py-3 md:gap-8 md:py-4'
                                                >
                                                    <span className='t-label w-6 shrink-0 text-lavender'>
                                                        0{index + 1}
                                                    </span>
                                                    <span className={cn(
                                                        'font-heading text-[clamp(2.4rem,8.4vw,7.25rem)] font-semibold uppercase leading-[0.92] tracking-[-0.03em]',
                                                        'transition-[font-variation-settings,opacity] duration-700 ease-out-expo [font-variation-settings:"wdth"_70]',
                                                        'group-hover:[font-variation-settings:"wdth"_125] group-focus-visible:[font-variation-settings:"wdth"_125]',
                                                        hovered !== 'default' && hovered !== link.href && 'opacity-35'
                                                    )}>
                                                        {link.label}
                                                    </span>
                                                </Link>
                                            </motion.div>
                                        </li>
                                    ))}
                                </ul>
                            </nav>

                            <div className='flex flex-col gap-12 lg:col-span-4 lg:pt-12'>
                                <nav aria-label='Company'>
                                    <p className='t-label mb-5 text-mist/50'>
                                        Company
                                    </p>

                                    <ul className='flex flex-col gap-1'>
                                        {companyLinks.map((link, index) => (
                                            <li key={link.href} className='overflow-hidden'>
                                                <motion.div
                                                    initial={{ y: '105%' }}
                                                    animate={{ y: '0%' }}
                                                    exit={{ y: '105%' }}
                                                    transition={{ duration: 0.8, ease: EASE_OUT_EXPO, delay: 0.45 + index * 0.05 }}
                                                >
                                                    <Link
                                                        href={link.href}
                                                        onClick={onClose}
                                                        className='group inline-flex items-center gap-3 font-heading text-3xl font-medium tracking-tight md:text-4xl'
                                                    >
                                                        <span className='link-line'>
                                                            {link.label}
                                                        </span>
                                                        <ArrowUpRight className='size-5 -translate-x-2 opacity-0 transition-all duration-500 group-hover:translate-x-0 group-hover:opacity-100' aria-hidden />
                                                    </Link>
                                                </motion.div>
                                            </li>
                                        ))}
                                    </ul>
                                </nav>

                                <motion.div
                                    className='flex flex-col gap-6'
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.8, ease: EASE_OUT_EXPO, delay: 0.6 }}
                                >
                                    <div>
                                        <p className='t-label mb-3 text-mist/50'>
                                            Talk to us
                                        </p>
                                        <a href={`mailto:${siteConfig.email}`} className='link-line text-xl font-medium'>
                                            {siteConfig.email}
                                        </a>
                                    </div>

                                    <OfficeClocks layout='stack' className='max-w-xs text-mist' />
                                </motion.div>
                            </div>
                        </div>

                        <motion.div
                            className='mt-16 flex flex-wrap items-center justify-between gap-6 border-t border-mist/10 pt-6'
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.6, delay: 0.7 }}
                        >
                            <div className='flex gap-6 t-label'>
                                <a href={siteConfig.links.linkedin} target='_blank' rel='noopener noreferrer' className='link-line'>
                                    LinkedIn
                                </a>
                                <a href={siteConfig.links.x} target='_blank' rel='noopener noreferrer' className='link-line'>
                                    X / Twitter
                                </a>
                            </div>

                            <Link
                                href='/contact'
                                onClick={onClose}
                                className='group inline-flex items-center gap-3 bg-violet px-6 py-4 text-sm font-semibold text-white transition-colors hover:bg-lavender hover:text-void'
                            >
                                Book a call
                                <ArrowUpRight className='size-4 transition-transform duration-500 group-hover:rotate-45' aria-hidden />
                            </Link>
                        </motion.div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}
